import { cn } from '@/lib/utils'

export interface DsSkeletonProps {
  width?: number | string
  height?: number | string
  radius?: number
  circle?: boolean
  className?: string
}

/** Stops shimmering under reduce-motion. */
export function DsSkeleton({ width, height = 16, radius = 8, circle, className }: DsSkeletonProps) {
  return (
    <span
      aria-hidden
      className={cn('ds-shimmer block', className)}
      style={{ width: circle ? height : width ?? '100%', height, borderRadius: circle ? 9999 : radius }}
    />
  )
}

export const DsSkeletonLine = ({ width, height = 12, className }: { width?: number | string; height?: number; className?: string }) => (
  <DsSkeleton width={width} height={height} radius={6} className={className} />
)

export const DsSkeletonCircle = ({ size = 40 }: { size?: number }) => <DsSkeleton height={size} circle />

export function DsSkeletonListRow({ leading = true, trailing = true }: { leading?: boolean; trailing?: boolean }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3" aria-hidden>
      {leading && <DsSkeletonCircle size={40} />}
      <div className="flex-1 space-y-2">
        <DsSkeletonLine width="60%" height={13} />
        <DsSkeletonLine width="40%" height={11} />
      </div>
      {trailing && <DsSkeletonLine width={56} height={13} />}
    </div>
  )
}

export function DsSkeletonCard({ imageHeight = 140 }: { imageHeight?: number }) {
  return (
    <div className="space-y-3 rounded-xl border bg-card p-4" aria-hidden>
      <DsSkeleton height={imageHeight} radius={10} />
      <DsSkeletonLine width="70%" height={14} />
      <DsSkeletonLine width="45%" />
    </div>
  )
}

export function DsSkeletonStat() {
  return (
    <div className="space-y-2 rounded-xl border bg-card p-4" aria-hidden>
      <DsSkeletonLine width={64} height={11} />
      <DsSkeleton width={110} height={28} radius={6} />
      <DsSkeletonLine width={80} />
    </div>
  )
}

export function DsSectionSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <DsSkeletonListRow key={i} />
      ))}
    </div>
  )
}
